import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useCreateDeviceRole,
  useCreateDeviceType,
  useCreateManufacturer,
  useCreateModuleType,
  useCreatePlatform,
  useDcimCatalog,
  type DcimCatalog,
  type DeviceTypeInsert,
} from '../hooks/useDcim';
import {
  AIRFLOWS,
  DEVICE_TYPE_KINDS,
  ROLE_COLOR_TOKENS,
  countByKind,
  deviceTypeLabel,
  summarizeCounts,
  type DeviceTypeKind,
} from '../lib/dcim';
import { TemplateEditor } from '../components/dcim/TemplateEditor';
import { BTN_PRIMARY, Feedback, INPUT, RoleDot, nullable, type FeedbackState } from '../components/dcim/ui';

/**
 * Device type library: each real-world model defined once, with every
 * interface, port and bay it ships with. Devices (/devices) are built from
 * these; the database copies the templates into each new device.
 */

type Tab = 'devices' | 'modules' | 'catalog';

export function DeviceTypesPage() {
  const { data, isLoading, isError } = useDcimCatalog();
  const [tab, setTab] = useState<Tab>('devices');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Device Types</h1>
          <p className="text-sm text-text-muted mt-1">
            {isLoading
              ? 'Loading…'
              : `${data?.deviceTypes.length ?? 0} device types · ${data?.moduleTypes.length ?? 0} module types · ${data?.manufacturers.length ?? 0} manufacturers`}
          </p>
        </div>
        <Link to="/devices" className="text-sm text-primary hover:underline">Devices →</Link>
      </div>

      <div role="tablist" aria-label="Device type library" className="flex gap-1 border-b border-border">
        {([['devices', 'Device types'], ['modules', 'Module types'], ['catalog', 'Manufacturers, platforms & roles']] as const).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`px-3 min-h-[44px] md:min-h-[36px] text-sm border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${tab === id ? 'border-primary text-text font-medium' : 'border-transparent text-text-muted hover:text-text'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {isError && (
        <p role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-notification">Could not load the device type library. Refresh to try again.</p>
      )}

      {data && tab === 'devices' && <DeviceTypesTab catalog={data} />}
      {data && tab === 'modules' && <ModuleTypesTab catalog={data} />}
      {data && tab === 'catalog' && <CatalogTab catalog={data} />}
    </div>
  );
}

// ─── Device types ───────────────────────────────────────────────────────────

function DeviceTypesTab({ catalog }: { catalog: DcimCatalog }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.deviceTypes
      .map((t) => ({ t, maker: catalog.manufacturerById.get(t.manufacturer_id)?.name ?? '' }))
      .filter(({ t, maker }) => !q || [maker, t.model, t.part_number, t.kind].filter(Boolean).some((v) => v!.toLowerCase().includes(q)))
      .sort((a, b) => a.maker.localeCompare(b.maker) || a.t.model.localeCompare(b.t.model, undefined, { numeric: true }));
  }, [catalog, search]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <label className="block sm:w-80">
          <span className="sr-only">Search device types</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search manufacturer, model, part number" className={INPUT} />
        </label>
        <button type="button" onClick={() => setCreating((v) => !v)} className={BTN_PRIMARY} aria-expanded={creating}>
          {creating ? 'Close' : 'New device type'}
        </button>
      </div>

      {creating && <NewDeviceTypeForm catalog={catalog} onCreated={(id) => { setCreating(false); setOpen(id); }} />}

      {catalog.manufacturers.length === 0 && (
        <p className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">Add a manufacturer on the “Manufacturers, platforms &amp; roles” tab first.</p>
      )}
      {rows.length === 0 && catalog.deviceTypes.length > 0 && <p className="text-sm text-text-muted">No device types match your search.</p>}

      <ul className="space-y-2">
        {rows.map(({ t, maker }) => {
          const templates = catalog.templatesByOwner.get(t.id) ?? [];
          const count = catalog.deviceCountByType.get(t.id) ?? 0;
          const isOpen = open === t.id;
          return (
            <li key={t.id} className="rounded-lg border border-border bg-surface">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : t.id)}
                aria-expanded={isOpen}
                className="w-full text-left p-3 min-h-[44px] flex flex-col md:flex-row md:items-center gap-1 md:gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <span className="md:w-72 min-w-0">
                  <span className="block text-sm font-medium text-text truncate">{deviceTypeLabel(maker, t.model)}</span>
                  <span className="block text-xs text-text-muted">
                    {t.kind.replace('_', ' ')} · {t.u_height}U{t.part_number ? ` · ${t.part_number}` : ''}{t.subdevice_role ? ` · ${t.subdevice_role}` : ''}
                  </span>
                </span>
                <span className="flex-1 text-xs text-text-muted">{summarizeCounts(countByKind(templates))}</span>
                <span className="text-xs text-text-faint whitespace-nowrap">{count} device{count === 1 ? '' : 's'}</span>
              </button>
              {isOpen && (
                <div className="border-t border-border p-3 bg-surface-2">
                  <TemplateEditor
                    owner={{ deviceTypeId: t.id }}
                    templates={templates}
                    allowDeviceBays={t.subdevice_role === 'parent'}
                    instanceCount={count}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NewDeviceTypeForm({ catalog, onCreated }: { catalog: DcimCatalog; onCreated: (id: string) => void }) {
  const create = useCreateDeviceType();
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const [form, setForm] = useState({
    manufacturer_id: catalog.manufacturers[0]?.id ?? '',
    model: '',
    part_number: '',
    kind: 'switch' as DeviceTypeKind,
    u_height: '1',
    is_full_depth: true,
    airflow: '',
    weight_kg: '',
    subdevice_role: '',
    default_platform_id: '',
    description: '',
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const v = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
    setMessage(null);
  };

  const submit = async () => {
    const u = Number(form.u_height);
    if (!form.manufacturer_id) return setMessage({ tone: 'error', text: 'Choose a manufacturer.' });
    if (!form.model.trim()) return setMessage({ tone: 'error', text: 'Model is required.' });
    if (!Number.isFinite(u) || u < 0 || u > 60 || u * 2 !== Math.floor(u * 2)) return setMessage({ tone: 'error', text: 'Height must be 0–60 in half-U steps.' });
    if (form.subdevice_role === 'child' && u !== 0) return setMessage({ tone: 'error', text: 'A child device sits in a bay, so its height must be 0U.' });
    const weight = form.weight_kg.trim() === '' ? null : Number(form.weight_kg);
    if (weight !== null && (!Number.isFinite(weight) || weight <= 0)) return setMessage({ tone: 'error', text: 'Weight must be a positive number.' });
    const row: DeviceTypeInsert = {
      manufacturer_id: form.manufacturer_id,
      model: form.model.trim(),
      part_number: nullable(form.part_number),
      kind: form.kind,
      u_height: u,
      is_full_depth: form.is_full_depth,
      airflow: nullable(form.airflow),
      weight_kg: weight,
      subdevice_role: (nullable(form.subdevice_role) as DeviceTypeInsert['subdevice_role']) ?? null,
      default_platform_id: nullable(form.default_platform_id),
      description: nullable(form.description),
    };
    try {
      onCreated(await create.mutateAsync(row));
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not create: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  return (
    <form className="rounded-lg border border-border bg-surface p-3 space-y-3" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-label="New device type">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Manufacturer">
          <select value={form.manufacturer_id} onChange={set('manufacturer_id')} className={INPUT} required>
            {catalog.manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Model"><input value={form.model} onChange={set('model')} className={INPUT} placeholder="USW-Pro-Max-48-PoE" required /></Field>
        <Field label="Part number"><input value={form.part_number} onChange={set('part_number')} className={INPUT} /></Field>
        <Field label="Kind">
          <select value={form.kind} onChange={set('kind')} className={INPUT}>
            {DEVICE_TYPE_KINDS.map((k) => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Height (U)" hint="0 for unracked gear (APs, cameras).">
          <input inputMode="decimal" value={form.u_height} onChange={set('u_height')} className={INPUT} />
        </Field>
        <Field label="Airflow">
          <select value={form.airflow} onChange={set('airflow')} className={INPUT}>
            <option value="">—</option>
            {AIRFLOWS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
        <Field label="Weight (kg)"><input inputMode="decimal" value={form.weight_kg} onChange={set('weight_kg')} className={INPUT} /></Field>
        <Field label="Subdevice role" hint="Parent: has bays for child devices.">
          <select value={form.subdevice_role} onChange={set('subdevice_role')} className={INPUT}>
            <option value="">None</option>
            <option value="parent">Parent</option>
            <option value="child">Child</option>
          </select>
        </Field>
        <Field label="Default platform">
          <select value={form.default_platform_id} onChange={set('default_platform_id')} className={INPUT}>
            <option value="">—</option>
            {catalog.platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Description"><input value={form.description} onChange={set('description')} className={INPUT} /></Field>
        <label className="flex items-center gap-2 text-sm text-text min-h-[44px] md:min-h-[40px] self-end">
          <input type="checkbox" checked={form.is_full_depth} onChange={set('is_full_depth')} />
          Full depth
        </label>
      </div>
      <button type="submit" disabled={create.isPending} className={BTN_PRIMARY}>{create.isPending ? 'Creating…' : 'Create device type'}</button>
      <Feedback message={message} />
    </form>
  );
}

// ─── Module types ───────────────────────────────────────────────────────────

function ModuleTypesTab({ catalog }: { catalog: DcimCatalog }) {
  const create = useCreateModuleType();
  const [open, setOpen] = useState<string | null>(null);
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const [form, setForm] = useState({ manufacturer_id: catalog.manufacturers[0]?.id ?? '', profile_id: '', model: '', part_number: '', description: '' });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setMessage(null);
  };

  const submit = async () => {
    if (!form.manufacturer_id || !form.model.trim()) return setMessage({ tone: 'error', text: 'Manufacturer and model are required.' });
    try {
      const id = await create.mutateAsync({
        manufacturer_id: form.manufacturer_id,
        profile_id: nullable(form.profile_id),
        model: form.model.trim(),
        part_number: nullable(form.part_number),
        description: nullable(form.description),
      });
      setForm((f) => ({ ...f, model: '', part_number: '', description: '' }));
      setOpen(id);
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not create: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const rows = [...catalog.moduleTypes].sort((a, b) =>
    (catalog.manufacturerById.get(a.manufacturer_id)?.name ?? '').localeCompare(catalog.manufacturerById.get(b.manufacturer_id)?.name ?? '') ||
    a.model.localeCompare(b.model, undefined, { numeric: true }),
  );
  const profileName = new Map(catalog.profiles.map((p) => [p.id, p.name]));

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-muted">
        Modules are field-replaceable parts (line cards, PSUs, transceivers) that go into a device's module bay. Use {'{module}'} in a
        component name and it becomes the bay's position when installed (e.g. <span className="font-mono">{'{module}'}/[1-24]</span> in bay A → A/1 … A/24).
      </p>
      <form className="rounded-lg border border-border bg-surface p-3 space-y-3" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-label="New module type">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Manufacturer">
            <select value={form.manufacturer_id} onChange={set('manufacturer_id')} className={INPUT}>
              {catalog.manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Model"><input value={form.model} onChange={set('model')} className={INPUT} required /></Field>
          <Field label="Part number"><input value={form.part_number} onChange={set('part_number')} className={INPUT} /></Field>
          <Field label="Profile">
            <select value={form.profile_id} onChange={set('profile_id')} className={INPUT}>
              <option value="">—</option>
              {catalog.profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Description"><input value={form.description} onChange={set('description')} className={INPUT} /></Field>
        </div>
        <button type="submit" disabled={create.isPending} className={BTN_PRIMARY}>{create.isPending ? 'Creating…' : 'Create module type'}</button>
        <Feedback message={message} />
      </form>

      <ul className="space-y-2">
        {rows.map((m) => {
          const templates = catalog.templatesByOwner.get(m.id) ?? [];
          const isOpen = open === m.id;
          return (
            <li key={m.id} className="rounded-lg border border-border bg-surface">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : m.id)}
                aria-expanded={isOpen}
                className="w-full text-left p-3 min-h-[44px] flex flex-col md:flex-row md:items-center gap-1 md:gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <span className="md:w-72 min-w-0">
                  <span className="block text-sm font-medium text-text truncate">{deviceTypeLabel(catalog.manufacturerById.get(m.manufacturer_id)?.name, m.model)}</span>
                  <span className="block text-xs text-text-muted">{[m.part_number, m.profile_id ? profileName.get(m.profile_id) : null].filter(Boolean).join(' · ') || 'Module'}</span>
                </span>
                <span className="flex-1 text-xs text-text-muted">{summarizeCounts(countByKind(templates))}</span>
              </button>
              {isOpen && (
                <div className="border-t border-border p-3 bg-surface-2">
                  <TemplateEditor owner={{ moduleTypeId: m.id }} templates={templates} allowDeviceBays={false} instanceCount={0} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Catalog ────────────────────────────────────────────────────────────────

function CatalogTab({ catalog }: { catalog: DcimCatalog }) {
  const addManufacturer = useCreateManufacturer();
  const addPlatform = useCreatePlatform();
  const addRole = useCreateDeviceRole();
  const [maker, setMaker] = useState('');
  const [platform, setPlatform] = useState({ name: '', manufacturer_id: '' });
  const [role, setRole] = useState({ name: '', color_token: 'info' });
  const [message, setMessage] = useState<FeedbackState | null>(null);

  const run = async (fn: () => Promise<unknown>, ok: string, reset: () => void) => {
    setMessage(null);
    try {
      await fn();
      reset();
      setMessage({ tone: 'success', text: ok });
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <div className="space-y-3">
      <Feedback message={message} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CatalogList title="Manufacturers" items={catalog.manufacturers.map((m) => ({ id: m.id, node: m.name }))}>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (maker.trim()) void run(() => addManufacturer.mutateAsync({ name: maker.trim() }), `Added ${maker.trim()}.`, () => setMaker('')); }}>
            <input value={maker} onChange={(e) => setMaker(e.target.value)} className={INPUT} placeholder="Ubiquiti" aria-label="New manufacturer" />
            <button type="submit" className={BTN_PRIMARY} disabled={addManufacturer.isPending}>Add</button>
          </form>
        </CatalogList>

        <CatalogList
          title="Platforms"
          items={catalog.platforms.map((p) => ({
            id: p.id,
            node: <>{p.name}{p.manufacturer_id && <span className="text-text-muted"> · {catalog.manufacturerById.get(p.manufacturer_id)?.name}</span>}</>,
          }))}
        >
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (platform.name.trim()) void run(() => addPlatform.mutateAsync({ name: platform.name.trim(), manufacturer_id: nullable(platform.manufacturer_id) }), `Added ${platform.name.trim()}.`, () => setPlatform({ name: '', manufacturer_id: '' })); }}>
            <input value={platform.name} onChange={(e) => setPlatform((p) => ({ ...p, name: e.target.value }))} className={INPUT} placeholder="UniFi OS" aria-label="New platform" />
            <div className="flex gap-2">
              <select value={platform.manufacturer_id} onChange={(e) => setPlatform((p) => ({ ...p, manufacturer_id: e.target.value }))} className={INPUT} aria-label="Platform manufacturer">
                <option value="">Any manufacturer</option>
                {catalog.manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button type="submit" className={BTN_PRIMARY} disabled={addPlatform.isPending}>Add</button>
            </div>
          </form>
        </CatalogList>

        <CatalogList title="Device roles" items={catalog.roles.map((r) => ({ id: r.id, node: <RoleDot name={r.name} token={r.color_token} /> }))}>
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (role.name.trim()) void run(() => addRole.mutateAsync({ name: role.name.trim(), color_token: role.color_token }), `Added ${role.name.trim()}.`, () => setRole({ name: '', color_token: 'info' })); }}>
            <input value={role.name} onChange={(e) => setRole((r) => ({ ...r, name: e.target.value }))} className={INPUT} placeholder="Access switch" aria-label="New role" />
            <div className="flex gap-2">
              <select value={role.color_token} onChange={(e) => setRole((r) => ({ ...r, color_token: e.target.value }))} className={INPUT} aria-label="Role colour">
                {ROLE_COLOR_TOKENS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button type="submit" className={BTN_PRIMARY} disabled={addRole.isPending}>Add</button>
            </div>
          </form>
        </CatalogList>
      </div>
    </div>
  );
}

function CatalogList({ title, items, children }: { title: string; items: { id: string; node: React.ReactNode }[]; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-surface" aria-label={title}>
      <header className="px-3 py-2 border-b border-border text-sm font-semibold text-text">{title} ({items.length})</header>
      <ul className="divide-y divide-divider max-h-72 overflow-y-auto">
        {items.length === 0 && <li className="px-3 py-2 text-xs text-text-muted">None yet.</li>}
        {items.map((i) => <li key={i.id} className="px-3 py-2 text-sm text-text">{i.node}</li>)}
      </ul>
      <div className="p-3 border-t border-border">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-text-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-text-faint mt-0.5">{hint}</span>}
    </label>
  );
}
